import React, { type ComponentProps } from "react";

import Interweave from "interweave";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { InterleaveSpans } from "@components/utilities";
import {
  type AlarmConditionV2,
  type AnomalyCondition,
  type Breakdown,
  formatting,
  isSimpleAlarmCondition,
  jsonHash,
  metrics,
  type MetricValueConfig,
  type Subject,
} from "@north-beam/nb-common";
import { MetricValue } from "@shared/metric-value";

export function FormatSubject(props: { subject: Subject }) {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const { subject } = props;

  const breakdownKeyToValues: Record<
    string,
    Record<string, string>
  > = React.useMemo(() => {
    const rv: Record<string, Record<string, string>> = {};
    for (const breakdown of breakdownConfigs) {
      const { key, choices } = breakdown;
      rv[key] = {};
      for (const { value, label } of choices) {
        rv[key][value] = label;
      }
    }
    return rv;
  }, [breakdownConfigs]);

  const breakdownToName: Record<string, string> = React.useMemo(() => {
    const rv: Record<string, string> = {};
    for (const breakdown of breakdownConfigs) {
      rv[breakdown.key] = breakdown.name;
    }
    return rv;
  }, [breakdownConfigs]);

  switch (subject.type) {
    case "all": {
      return <p>All campaigns (blended)</p>;
    }
    case "individuals": {
      return <p>Individual campaigns</p>;
    }
    case "groupedBy": {
      const { param } = subject;
      return (
        <React.Fragment>
          <p>
            Campaigns by{" "}
            <InterleaveSpans
              text={param.map((v) => breakdownToName[v.key] ?? v.key)}
              sep=", "
              Wrapper={Strong}
            />{" "}
            (blended):
          </p>
          <PropertyTable
            breakdowns={param}
            breakdownKeyToValues={breakdownKeyToValues}
          />
        </React.Fragment>
      );
    }
    case "individualsMatching": {
      const { param } = subject;
      return (
        <>
          <p>Active individual campaigns matching:</p>
          <PropertyTable
            breakdowns={param}
            breakdownKeyToValues={breakdownKeyToValues}
          />
        </>
      );
    }
  }
}

function PropertyTable({
  breakdownKeyToValues,
  breakdowns,
}: {
  breakdownKeyToValues: Record<string, Record<string, string>>;
  breakdowns: Breakdown[];
}) {
  return (
    <table className="table table-sm table-bordered" style={{ fontSize: 12 }}>
      <thead className="thead-light">
        <tr>
          <th>Key</th>
          <th>Values</th>
        </tr>
      </thead>
      <tbody>
        {breakdowns.map(({ key, values }) => (
          <tr key={key}>
            <td>{key}</td>
            <td>
              <ul>
                {values.map((v) => (
                  <li key={v}>{breakdownKeyToValues[key]?.[v] ?? v}</li>
                ))}
              </ul>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FormatMetricValueConfig({
  capitalized,
  config,
  metric,
}: {
  capitalized: boolean;
  config: MetricValueConfig;
  metric: metrics.MetricFragment;
}) {
  if (config.type === "FixedValueConfig") {
    return (
      <MetricValue
        as="span"
        className="font-bold"
        value={config.value}
        format={metric.format}
        precision="exact"
      />
    );
  }
  const { windowEndOffsetDays, windowStartOffsetDays } = config;
  const article = capitalized ? "The" : "the";
  return (
    <Interweave
      content={`${article} average ${metric.name} between <b>${windowStartOffsetDays} days ago</b> and <b>${windowEndOffsetDays} days ago</b>`}
    />
  );
}

function FormatAnomalyCondition({
  anomalyCondition,
}: {
  anomalyCondition: AnomalyCondition;
}) {
  if (anomalyCondition.type === "OutsidePercentageOfReferenceValue") {
    return (
      <Interweave
        content={`deviates by more than <b>${formatting.formatMetric.exact(
          anomalyCondition.percentage,
          metrics.MetricTypes.PERCENTAGE,
          {},
        )}</b>`}
      />
    );
  }

  if (anomalyCondition.type === "AboveReferenceValue") {
    return <>goes above</>;
  }

  return <>goes below</>;
}

export function FormatAlarmCondition(props: {
  alarmCondition: AlarmConditionV2;
}) {
  const { alarmCondition } = props;
  if (isSimpleAlarmCondition(alarmCondition)) {
    const { anomalyCondition, currentValue, referenceValue, metric } =
      alarmCondition;

    return (
      <>
        <FormatMetricValueConfig
          capitalized={true}
          metric={metric}
          config={currentValue}
        />{" "}
        <FormatAnomalyCondition anomalyCondition={anomalyCondition} />{" "}
        <FormatMetricValueConfig
          capitalized={false}
          metric={metric}
          config={referenceValue}
        />
      </>
    );
  } else {
    const { combinator, children } = alarmCondition;

    const text =
      combinator === "and"
        ? "All of the following"
        : "At least one of the following";

    return (
      <>
        {text}:
        <ul>
          {children.map((child) => (
            <li key={jsonHash(child)}>
              <FormatAlarmCondition alarmCondition={child} />{" "}
            </li>
          ))}
        </ul>
      </>
    );
  }
}

const Strong = ({ children, ...rest }: ComponentProps<"strong">) => (
  <strong {...rest}>{children}</strong>
);
